//services
//reducers
import { getTranfserAmount } from 'state/reducers/balance.reducers';
import { selectImageUrl, selectTranslations } from 'state/reducers/translation.reducers';

//components
import { FormCard } from 'theme/elements/Forms';
import { FormHeader1, HeaderCaption1, HeaderPage4 } from 'theme/elements/Headers';
//sections
//mui
import CheckCircleOutlineRounded from '@mui/icons-material/CheckCircleOutlineRounded';
import UploadFile from '@mui/icons-material/UploadFile';
import { Box, Button, Stack, Typography, useTheme } from '@mui/material';
//constants
import { balanceTransStepsPrefix } from 'constants/balance.constants';

import FormCreator from 'components/FormCreator';
import { fieldsGroupType, fieldType, invalidFields } from 'components/FormCreator/types';
import { useAppSelector } from 'hooks/redux.hooks';
import { manualFieldsValidations } from '../../../shared/steps/step2/index';
import { getExistingBankAccounts } from 'state/reducers/bank-account.reducers';
import React, { useMemo, useState } from 'react';
import { getCurrenciesObj } from 'state/reducers/global.reducers';
import { FormButton } from 'theme/elements/Buttons';
import ErrorBox from 'components/ErrorBox';
import { acceptableFileFormats, maxFileSize } from 'constants/global.constants';
import { getRawAmountInputValue, validateFileExtensions } from 'utils';

export default function CurrencyApprovalForm(props: {
	handleFormChange: any;
	invalidFields: invalidFields;
	handleTransferProofFile: any;
	isFileUploaded: boolean;
}) {
	const t = useAppSelector(selectTranslations);
	const imageUrl = useAppSelector(selectImageUrl);
	const [amount, setAmount] = useState<string>('');
	const transferAmount = useAppSelector(getTranfserAmount);
	const existBankAccounts = useAppSelector(getExistingBankAccounts);
	const currencies = useAppSelector(getCurrenciesObj);
	const theme = useTheme();
	const [fileName, setFileName] = useState<string | null>(null);
	const [showErrorBox, setShowErrorBox] = useState(false);
	const localCurrencyId = useMemo(() => {
		return existBankAccounts?.length && existBankAccounts[0].currencyId;
	}, [existBankAccounts]);
	const [errorText, setErrorText] = useState<string | null>(null);

	const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		let { name, value } = event.target;
		value = getRawAmountInputValue(value);
		setAmount(value);
		props.handleFormChange({ name, value });
	};

	const currentAmount = useMemo(() => {
		return transferAmount || amount;
	}, [amount, transferAmount]);

	const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file: File = event.target.files![0];

		setShowErrorBox(false);
		if (file) {
			if (validateFileExtensions(file.type)) {
				if (file.size <= maxFileSize) {
					setFileName(file.name);
					props.handleTransferProofFile(file);
				} else {
					props.handleTransferProofFile(null);
					setErrorText(t(`servers.errors.codes.1302`));
					setShowErrorBox(true);
				}
			} else {
				setErrorText(t(`errors.files.upload.file_type_not_supported.title`));
				props.handleTransferProofFile(null);
				setShowErrorBox(true);
			}
		}
	};
	return (
		<Box component="form">
			<Stack marginTop="32px">
				<FormCard>
					<Stack spacing={4}>
						<FormHeader1
							title={t(`${balanceTransStepsPrefix}.2.currency_approval.header.title`)}
							text={t(`${balanceTransStepsPrefix}.2.currency_approval.header.details`)}
						/>

						<FormCreator
							initialValidation={props.invalidFields}
							name="newBankAccount"
							handleChange={handleAmountChange}
							validations={manualFieldsValidations}
							fieldsGroups={[
								{
									type: fieldsGroupType.col,
									spacing: 2,
									fields: [
										{
											type: fieldType.text,
											props: {
												disabled: true,
												name: 'currency',
												label: t('page.balance.add.step.2.currency_approval.currency.text'),
												value: currencies[localCurrencyId].name,
											},
										},
										{
											type: fieldType.text,
											props: {
												required: true,
												formatAsAmount: true,
												name: 'amount',
												label: t(`${balanceTransStepsPrefix}.2.currency_approval.amount.text`),
												value: currentAmount,
												placeholder: t(
													`${balanceTransStepsPrefix}.2.currency_approval.amount.placeholder`
												),
											},
										},
									],
								},
							]}
						/>
						<Stack spacing={1}>
							<Stack direction="column" overflow="hidden">
								<HeaderCaption1
									title={t(
										`${balanceTransStepsPrefix}.2.currency_approval.reference_doc.header.title`
									)}
									text={t(
										`${balanceTransStepsPrefix}.2.currency_approval.reference_doc.header.details`
									)}
								/>
								<Stack
									direction="row"
									marginTop="32px"
									alignItems="center"
									justifyContent="space-between"
									sx={{
										backgroundColor: theme.palette.root.brandSecondary_012,
										padding: '32px',
										borderRadius: '8px',
										[theme.breakpoints.down('sm')]: {
											padding: '16px',
										},
									}}
								>
									{!props.isFileUploaded ? (
										<HeaderPage4
											title={t(
												`${balanceTransStepsPrefix}.2.currency_approval.reference_doc.no_file_selected.title`
											)}
											text={t(
												`${balanceTransStepsPrefix}.2.currency_approval.reference_doc.no_file_selected.text`
											)}
										/>
									) : (
										<Typography sx={{ overflowWrap: 'break-word', maxWidth: '50%' }}>
											{fileName}
										</Typography>
									)}
									<FormButton>
										<Button
											variant="outlined"
											sx={{
												'&.MuiButton-outlined': {
													borderColor: theme.palette.root.basicDeepGreen,
												},
											}}
											component="label"
											startIcon={
												props.isFileUploaded
													? imageUrl(
															'page.balance.add.step2.currency_approval_form.file_upload_button.success.icon'
													  ) || <CheckCircleOutlineRounded />
													: imageUrl(
															'page.balance.add.step2.currency_approval_form.file_upload_button.upload.icon'
													  ) || <UploadFile />
											}
										>
											{t(
												`${balanceTransStepsPrefix}.2.currency_approval.reference_doc.file_upload_button.text`
											)}
											<input
												type="file"
												onChange={handleFileUpload}
												hidden
												accept={acceptableFileFormats}
											/>
										</Button>
									</FormButton>
								</Stack>
							</Stack>
							{showErrorBox && (
								<ErrorBox
									icon={
										<img
											src={
												imageUrl(
													'page.balance.add.step2.currency_approval_form.upload.fail.icon'
												) || '/icons/file_error.svg'
											}
										/>
									}
									text={errorText}
								/>
							)}
						</Stack>
					</Stack>
				</FormCard>
			</Stack>
		</Box>
	);
}
